// Abandoning a pre-charge attempt from the wizard: one confirmation, one
// server-side cancel.
//
// ActionButtonWrapper's built-in 'cancel' action confirms with "Are you sure?"
// and then only resets LOCAL state -- the server keeps the attempt pending and
// holding the connector. Every explicit abandonment (Cancel, a leaving header
// Back, "Start again") goes through here instead, which uses
// SessionContext.cancelSession, the existing cancellation path: server-side
// cancel via cancelAndCleanup (voiding any card hold), then the wizard resets.
//
// Never cancels a session that has started charging. cancelSession refuses on
// LOCAL signs of charging, but local state can lag the charger (the plug-in
// screen learns of StartTransaction by polling), and the server's cancel route
// turns a cancel on a started session into a stop. So the server's own view is
// read first; if it says charging has begun, nothing is cancelled or reset.
//
// Single-flight across the whole app: a double tap, or two controls on the
// same screen (header Back and Cancel), cannot issue a second cancel while one
// is in flight. The server cancel is idempotent as well.
import React, { useCallback } from 'react';
import { FaCircleInfo } from 'react-icons/fa6';
import { useSession } from '../context/SessionContext.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { getCustomerSession } from '../api/sessionsApi.js';
import { abortActivePreauth } from '../utils/preauthRegistry.js';

// Server statuses that mean charging is under way (sw-ev-api
// ACTIVE_CHARGING_STATUSES); a started_at or tx_id means the same.
const CHARGING_STATUSES = ['charging', 'active', 'started'];

const CHARGING_STARTED = {
  success: false,
  error: 'Charging already started — please stop session instead.',
  blockReason: 'charging_started',
};

/**
 * Whether the server says this attempt has started charging: true, false, or
 * null when it could not be read. null is not "started": cancelSession then
 * applies its own local guard, and the server refuses to pre-start-cancel an
 * actively charging session.
 */
async function serverSaysStarted(sessionUid) {
  try {
    const r = await getCustomerSession(sessionUid);
    const s = r?.ok ? r.session : null;
    if (!s) return null;
    return Boolean(s.started_at || s.tx_id) || CHARGING_STATUSES.includes(s.status);
  } catch {
    return null;
  }
}

let inFlight = null;

export function useCancelAttempt() {
  const { state, cancelSession } = useSession();
  const { notify } = useNotification();
  const { showToast } = useToast();
  const sessionUid = state?.sessionUid || null;

  const cancelNow = useCallback(() => {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      const started = sessionUid ? await serverSaysStarted(sessionUid) : null;
      if (started === true) return CHARGING_STARTED;
      // A card authorization may be running: stop waiting on it. The server
      // cancel below makes any approval that still arrives void itself
      // (sw-ev-api payments/threeDSAttempts.js), so cancelling mid-authorize
      // is safe and leaves no hold behind.
      abortActivePreauth();
      // The server has just said charging has not started, so a local
      // 'Preparing' (cable plugged in before paying) must not refuse the
      // cancel — that left Cancel doing nothing at the card step.
      return cancelSession({ serverSaysNotStarted: started === false });
    })().finally(() => { inFlight = null; });
    return inFlight;
  }, [sessionUid, cancelSession]);

  /** Tell the driver when a cancel did not go through. */
  const reportResult = useCallback((result) => {
    if (!result || result.success) return;
    showToast({
      message: result.error || 'Failed to cancel session',
      type: 'error',
    });
  }, [showToast]);

  const confirmAndCancel = useCallback(() => {
    notify({
      title: (
        <span>
          <FaCircleInfo style={{ marginRight: '.5rem' }} />
          Are you sure?
        </span>
      ),
      message: 'This will cancel and reset your session.',
      action: { label: 'Yes', onClick: () => { cancelNow().then(reportResult); } },
    });
  }, [notify, cancelNow, reportResult]);

  return { confirmAndCancel, cancelNow, reportResult };
}

/** Test seam: whether a cancel is currently in flight. */
export const __cancelInFlight = () => inFlight !== null;
